import { bullets, h, img, p } from '@/content/blocks'
import type { Project } from '@/types/content'

const project: Project = {
  slug: 'chess-bot',
  title: 'Chess Bot',
  summary: 'An autonomous gantry-driven chess robot.',
  cover: {
    src: '/assets/ChessBot-Assembly.png',
    alt: 'CAD assembly of the gantry chess robot',
  },
  tags: ['CAD', 'Manufacturing', 'Robotics'],
  order: 2,
  blocks: [
    p(
      'Over the second term of freshman year, my group and I have been working on a chess robot as part of a course project. The design challenge was open-ended, allowing us to create a robot of our liking given that the complexity to satisfy all criteria was present. One interest that came to mind was building a gantry which had been one of my ideas for a personal project except it needed a functional objective, hence we decided on chess.',
    ),
    p(
      'Given the broad nature of the project, an engineering design process was used to define the development of the project over the course term. The first stage of this process entailed planning a needs analysis on the problem, where the scope of the robot was defined. During a needs analysis, the constraints, functional requirements, and non-functional requirements determine the baseline of what the robot should/must accomplish and serve as the basis for evaluating the successfulness of the design.',
    ),
    p('For our project the requirements were defined as follows:'),

    bullets(
      [
        'The robot successfully plays chess without any human intervention',
        'The robot will move in three cartesian axes: the x, y, and z',
      ],
      { title: 'Functional Requirements:' },
    ),
    bullets(
      [
        'The robot should be able to perform chess movements without any human intervention',
        'The design should be easy to operate and have a fast startup and setup time',
        'The design should be safe to use and not present any potential hazards during operations',
      ],
      { title: 'Non-Functional Requirements:' },
    ),
    bullets(
      [
        'The robot must be cost effective and the total cost should be under $200',
        'The robot must be easy to transport and thus weigh under 10kg in its fully assembled state',
      ],
      { title: 'Constraints:' },
    ),

    img(
      '/assets/CB-G-CompletedAssem.png',
      'Completed CAD assembly of the chess robot: a rigid XY gantry frame carrying the compliant Z-axis over the chess board',
      { maxWidth: '850px' },
    ),
    p(
      'The next process that followed was conceptual design where we ideated on potential designs that would address the requirements. We had several designs but ultimately settled on one that we liked the most through a weighted decision matrix. Ultimately, the design drew inspiration from the layout of core-XY or other convention layout 3D-Printer. However instead of a rigid vertical Z-axis, we went with a rigid XY setup and compliant Z-Axis since most of the distance would be covered along the X and Y.',
    ),

    h('Z-Drive Axis'),
    p(
      'The z-axis controls the vertical degrees of freedom of the end effector through a carriage element. The z-axis is a belt driven mechanism where 20T GT2 pulley is locked onto the large EV3 motor controlled the driving motion and passed through an idler pulley for fluid motion. To address any slippage in the belt of the part, the belts are separately joined on the carriage plate to provide a passive tensioning system for the belts. In the first iteration of the design, the pulley was offset which caused the carriage to slant and needed a redesign. In the latest iteration, the pulley is centred such that the forces on the carriage can propel the carriage component fluidly without any drag. Another issue faced with the Z gantry is slanting that occurred in one of the designs of the part, where the load of the carriage was much heavier on one side than the other despite having a tolerance of less than 0.5mm enclosed between the plate and the bearings. This meant that the issue did not pertain a lack of precision in tolerancing but rather a different solution needed to be implemented. One observation was that the plate would no longer be aligned, in other words, the direction of the bearing would no longer be perpendicular to the plate. So, a way to simply inhibit the displacement of the plate was to add another bearing to that would constrain its degrees of freedom (DoF) to be strictly parallel to the plate.',
    ),
    img(
      '/assets/CB_Assem_Z-Axis.png',
      'Z-axis assembly with the belt-driven carriage, centred 20T GT2 pulley on the EV3 motor, and idler pulley',
      { maxWidth: '850px' },
    ),

    h('End Effector (Redesign)'),
    p(
      'The original end effector design used a linkage based system guided by a 1/4" bolt to lock additional DoF. There were multiple iterations made for this end effector but when the part was trialed in assembly level, we found that the motor did not have sufficient torque to power the part. So we needed to redesign the end effector in a constrained time frame with consideration that it must be drivable by the EV3 motors and that it must be resetable (to a default positioning) with high repeatability.',
    ),
    img(
      '/assets/CB_Assem_Claw.png',
      'Redesigned end effector claw assembly, sized to be driven by an EV3 motor and reset to a default position',
      { maxWidth: '550px' },
    ),

    h('Y-Drive Axis'),
    p(
      "The Y-Axis mechanism of the chess robot is designed to move the Z-gantry laterally. It features a directly driven motor without any reduction, contrasting with the X-Axis, which bears a larger portion of the gantry's load. This design choice for the Y-Axis enhances the robot's lateral speed, although the motor's power limitations result in a perception of slowness even at full power. Despite its simplicity, the Y-Axis mechanism effectively fulfills its role and facilitates the integration of software, making it a straightforward yet efficient component of the robot's overall design",
    ),
    img(
      '/assets/CB_Assem_Y-Axis.png',
      'Y-axis assembly showing the directly driven motor that carries the Z-gantry laterally along the rail',
      { maxWidth: '550px' },
    ),
  ],
}

export default project
